import { useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Alert,
    Divider,
    Tooltip,
} from '@mui/material';
import { API_URL } from '../api';
import AccountStatusChips from './AccountStatusChips';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const ACCOUNT_OPTIONS = [
    { value: 'pending_approval', label: 'Pending approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'declined', label: 'Declined' },
    { value: 'archived', label: 'Archived' },
];
const engagementOptions = (role) => [
    { value: 'enrolled', label: role === 'teacher' ? 'Teaching' : 'Enrolled' },
    { value: 'not_enrolled', label: role === 'teacher' ? 'Not teaching' : 'Not enrolled' },
    { value: 'on_break', label: 'On break' },
];

const fmt = (d) => (d ? new Date(d).toLocaleString('en-GB') : '');

// Turn schema enum values ("pending_approval", "not_enrolled") into
// human labels ("Pending approval", "Not enrolled") for the history timeline.
const humanStatus = (v) => {
    if (!v) return v;
    return String(v)
        .replace(/_/g, ' ')
        .replace(/^./, (c) => c.toUpperCase());
};

/**
 * Account-status card: current chips + history timeline + inline setter.
 *
 * Props:
 *   account     required — the account object (with statusHistory)
 *   onChanged   called after a successful save with the updated account
 *
 * Saves go through PATCH /api/admin/accounts/:id/status which validates
 * both enums and stamps history entries via Account.changeStatus().
 */
const AccountStatusCard = ({ account, onChanged }) => {
    const [accountStatus, setAccountStatus] = useState(account.accountStatus);
    const [engagementStatus, setEngagementStatus] = useState(account.engagementStatus);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const dirty =
        accountStatus !== account.accountStatus ||
        engagementStatus !== account.engagementStatus;

    const save = async () => {
        setBusy(true);
        setError('');
        setMessage('');
        try {
            const body = { reason: reason.trim() || undefined };
            if (accountStatus !== account.accountStatus) body.accountStatus = accountStatus;
            if (engagementStatus !== account.engagementStatus) body.engagementStatus = engagementStatus;
            const { data } = await axios.patch(
                `${API_URL}/api/admin/accounts/${account._id}/status`,
                body,
                authHeaders()
            );
            setMessage('Status updated.');
            setReason('');
            onChanged?.(data?.account);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const history = (account.statusHistory || []).slice().reverse();

    return (
        <Card sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Account status</Typography>
                <AccountStatusChips account={account} />
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                    select size="small" label="Account status"
                    value={accountStatus}
                    onChange={(e) => setAccountStatus(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    {ACCOUNT_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select size="small" label="Engagement"
                    value={engagementStatus}
                    onChange={(e) => setEngagementStatus(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    {engagementOptions(account.role).map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small" label="Reason (optional, audit)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    sx={{ flex: 1 }}
                />
                <Tooltip title={dirty ? 'Save status change' : 'Change a value to enable'}>
                    <span>
                        <Button
                            variant="contained"
                            onClick={save}
                            disabled={busy || !dirty}
                        >
                            {busy ? 'Saving…' : 'Save'}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>History</Typography>
            {history.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No status changes recorded yet.
                </Typography>
            )}
            <Stack spacing={1}>
                {history.map((h, i) => (
                    <Box key={i} sx={{
                        display: 'flex',
                        gap: 2,
                        py: 0.5,
                        borderBottom: i === history.length - 1 ? 'none' : '1px solid',
                        borderColor: 'divider',
                    }}>
                        <Typography variant="caption" sx={{ minWidth: 150, color: 'text.secondary' }}>
                            {fmt(h.at)}
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">
                                <strong>{h.field === 'accountStatus' ? 'Account' : 'Engagement'}</strong>
                                {h.from ? ` · ${humanStatus(h.from)} → ${humanStatus(h.to)}` : ` · → ${humanStatus(h.to)}`}
                            </Typography>
                            {h.reason && (
                                <Typography variant="caption" color="text.secondary">
                                    Reason: {h.reason}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Card>
    );
};

export default AccountStatusCard;
